import { Link } from "react-router-dom";
import { useSeo } from "../../lib/seo";
import { GuideLayout, H2, P, Check } from "./GuideLayout";

export function KatzeVermisstPage() {
  useSeo({
    title: "Katze vermisst: Was du jetzt tun solltest (Checkliste)",
    description:
      "Katze entlaufen? Die Checkliste für die ersten 24 Stunden: Umgebung absuchen, Nachbarn informieren, Register melden, Plakate aufhängen. Plus: was ab Tag 2 hilft und wie du vorbeugst.",
    path: "/katze-vermisst",
  });

  return (
    <GuideLayout
      eyebrow="Ratgeber · Notfall"
      title="Katze vermisst: Was du jetzt tun solltest"
      intro="Die meisten Katzen tauchen wieder auf, und zwar näher als man denkt. Entscheidend ist, dass du in den ersten Stunden systematisch vorgehst statt planlos zu suchen. Diese Checkliste führt dich durch."
    >
      <H2>Sofort: die ersten Stunden</H2>
      <P>
        Freigänger, die nicht heimkommen, sitzen oft nur wenige hundert Meter entfernt: eingesperrt in einer Garage, einem Keller oder Schuppen, oder verängstigt unter einem Auto. Wohnungskatzen, die zum ersten Mal draußen sind, entfernen sich meist noch weniger weit und verstecken sich.
      </P>
      <Check
        items={[
          <><strong>Zuhause und Grundstück absuchen.</strong> Schränke, Keller, Dachboden, Waschmaschine, Garage, Gartenschuppen, unter Terrassen und Hecken. Leise vorgehen, ruhig rufen.</>,
          <><strong>In der Dämmerung und nachts rufen.</strong> Dann ist es still und die Katze traut sich eher hervor. Die Leckerli-Dose schütteln, ein Lieblingsgeräusch nutzen.</>,
          <><strong>Vertrauten Geruch nach draußen legen.</strong> Getragene Kleidung, die Decke oder das benutzte Katzenklo vor die Tür. Das hilft der Katze, den Weg zurück zu finden.</>,
          <><strong>Nachbarn persönlich ansprechen.</strong> Bitte alle im Umkreis, Garagen, Keller und Schuppen zu öffnen und nachzusehen. Das ist der häufigste Fundort.</>,
          <><strong>Eine Tür oder Katzenklappe offen lassen.</strong> Viele Katzen kommen nachts von allein zurück, wenn der Weg frei ist.</>,
        ]}
      />

      <H2>Am ersten Tag: Meldungen und Sichtbarkeit</H2>
      <Check
        items={[
          <><strong>Im Haustierregister als vermisst melden.</strong> Ist deine Katze gechippt und registriert (zum Beispiel bei Tasso oder Findefix), melde sie dort als vermisst. Beide Register sind kostenlos.</>,
          <><strong>Tierheim, Tierärzte und Fundbüro anrufen.</strong> Gefundene Tiere werden oft dort abgegeben. Nenne Aussehen, Chipnummer und den Ort des Verschwindens.</>,
          <><strong>Plakate aufhängen.</strong> Aktuelles Foto, auffällige Merkmale, Ort und Datum, Telefonnummer. An Laternen, in Supermärkten, beim Tierarzt, im Umkreis von etwa einem Kilometer.</>,
          <><strong>Lokale Gruppen und Kleinanzeigen nutzen.</strong> Facebook-Gruppen deiner Stadt, Nachbarschafts-Apps und die Kategorie „Vermisste Tiere“ bei Kleinanzeigen. Immer mit Foto und Ort.</>,
          <><strong>Ist die Katze bei petloc angelegt?</strong> Dann setze im Konto den Status auf „vermisst“. Jeder, der den QR-Code scannt, sieht sofort den Hinweis, und du kannst das Vermisst-Plakat als PDF mit Foto und QR-Code direkt ausdrucken.</>,
        ]}
      />

      <H2>Ab Tag zwei: Radius vergrößern, Fallen stellen</H2>
      <P>
        Wenn die Katze nach 48 Stunden nicht zurück ist, weite die Suche aus. Verängstigte Katzen wandern in der Regel nachts und bleiben tagsüber versteckt. Frag im Tierschutzverein nach einer Lebendfalle, sie werden oft verliehen. Stell die Falle dort auf, wo die Katze zuletzt gesehen wurde, mit stark riechendem Futter, und kontrolliere sie regelmäßig.
      </P>
      <P>
        Halte Plakate und Online-Anzeigen aktuell und reagiere auf jede Sichtung, auch wenn sie unwahrscheinlich klingt. Viele Katzen werden nach Wochen wiedergefunden, weil jemand beharrlich geblieben ist.
      </P>

      <H2>Wenn sie wieder da ist</H2>
      <P>
        Ruhig empfangen, Wasser und Futter anbieten, auf Verletzungen und Gewicht achten. Bei Auffälligkeiten zum Tierarzt. Und dann die Vermisst-Meldungen in Registern, Gruppen und bei petloc wieder zurücknehmen, damit niemand weitersucht.
      </P>

      <H2>So kommt sie beim nächsten Mal schneller nach Hause</H2>
      <P>
        Ein Chip ist wichtig, hilft aber nur, wenn jemand die Katze einfängt und zum Tierarzt bringt. Die meisten Finder wollen helfen, wissen aber nicht, wem das Tier gehört, und trauen sich nicht, es mitzunehmen. Genau diese Lücke schließt ein QR-Code am Halsband: Der Finder scannt mit dem Handy, du bekommst Standort und Nachricht, ohne dass deine Telefonnummer sichtbar ist.
      </P>
      <Check
        items={[
          <><strong>Chip plus Registrierung</strong> als Basis, aktuell gehalten.</>,
          <><strong>QR-Sicherheitshalsband oder Mini-Anhänger</strong> für den direkten Kontakt durch den Finder. Sicherheitsverschluss, damit die Katze nirgends hängen bleibt.</>,
          <><strong>Aktuelles Foto</strong> griffbereit, am besten im Tierprofil, aus dem du im Notfall das Plakat erzeugst.</>,
        ]}
      />
      <P>
        Mehr zu den Unterschieden zwischen Chip, Adressmarke, QR-Anhänger und GPS-Tracker liest du im <Link to="/qr-anhaenger-vs-tasso-gps" className="text-brand-600 font-semibold">Vergleich</Link>.
      </P>
    </GuideLayout>
  );
}
